import { useEffect, useState } from "react";
import { useSearchParams } from "react-router";
import { Search, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

/**
 * Debounced search input that mirrors `?search=` in the URL. 300ms feels
 * snappy on desktop and keeps mobile keystrokes from spamming the
 * customer list endpoint.
 *
 * The input is uncontrolled-ish: its initial value comes from the URL on
 * mount (via the `useState` lazy initialiser); thereafter the local state
 * owns the value and the URL is the derived view (debounced). We
 * deliberately do NOT mirror the URL back into local state on every
 * render — that would force the input to fight the user mid-keystroke.
 */
export function CustomerSearchBar() {
  const [searchParams, setSearchParams] = useSearchParams();
  // Lazy initialiser reads the URL once at mount; subsequent URL changes
  // (e.g. back-button) do NOT push back into this state — that's a
  // deliberate trade-off so typing is never interrupted.
  const [value, setValue] = useState(() => searchParams.get("search") ?? "");

  // Debounce writes back to the URL. We use a setTimeout so typing fast
  // doesn't trigger one query per keystroke.
  useEffect(() => {
    const t = window.setTimeout(() => {
      const current = searchParams.get("search") ?? "";
      const trimmed = value.trim();
      if (trimmed === current) return;
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          if (trimmed === "") {
            params.delete("search");
          } else {
            params.set("search", trimmed);
          }
          // Reset page when the search changes — keeps the user at page 0
          // of the new result set rather than landing on an empty page.
          params.delete("page");
          return params;
        },
        { replace: true },
      );
    }, 300);
    return () => window.clearTimeout(t);
  }, [value, searchParams, setSearchParams]);

  return (
    <div className="relative flex w-full items-center sm:max-w-sm">
      <Search
        aria-hidden="true"
        className="pointer-events-none absolute left-3 size-4 text-muted-foreground"
      />
      <Input
        type="search"
        inputMode="search"
        aria-label="Tìm theo tên hoặc số điện thoại"
        placeholder="Tìm tên hoặc số điện thoại"
        value={value}
        onChange={(e) => setValue(e.currentTarget.value)}
        className="pl-9 pr-9"
      />
      {value.length > 0 ? (
        <Button
          variant="ghost"
          size="icon"
          aria-label="Xóa tìm kiếm"
          className="absolute right-1 size-7"
          onClick={() => setValue("")}
        >
          <X aria-hidden="true" className="size-3.5" />
        </Button>
      ) : null}
    </div>
  );
}
